import React, { useState } from 'react';
import WaitlistPage from './components/WaitlistPage';
import AboutUs from './components/AboutUs';
import OurStory from './components/OurStory';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsConditions from './components/TermsConditions';
import Header from './components/Header';
import Hero from './components/Hero';
import Quiz from './components/Quiz';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import Shop from './components/Shop';
import ComingSoon from './components/ComingSoon';
import ComingSoonLanding from './components/ComingSoonLanding';
import CookieConsent from './components/CookieConsent';

function App() {
  const [currentView, setCurrentView] = useState('coming-soon'); // Start with coming soon page
  const [sauceProfile, setSauceProfile] = useState(null);

  const handleQuizComplete = (results: any) => {
    setSauceProfile(results.sauceProfile);
    setCurrentView('dashboard');
  };

  const handleStartQuiz = () => {
    setCurrentView('quiz');
  };

  const handleEnterApp = () => {
    // Go directly to hero page (no login required)
    setCurrentView('hero');
  };

  const handleGoToShop = () => {
    setCurrentView('shop');
  };

  const handleBackToWaitlist = () => {
    setCurrentView('waitlist');
    // Scroll to top when navigating back to waitlist
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToComingSoon = () => {
    setCurrentView('coming-soon');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewChange = (view: string) => {
    // Handle navigation logic based on user state and destination
    switch (view) {
      case 'waitlist':
        setCurrentView('waitlist');
        break;
        
      case 'hero':
        setCurrentView('hero');
        break;
        
      case 'quiz':
        setCurrentView('quiz');
        break;
        
      case 'dashboard':
        if (!sauceProfile) {
          // If no profile exists, redirect to quiz first
          setCurrentView('quiz');
        } else {
          setCurrentView('dashboard');
        }
        break;
        
      case 'profile':
        if (!sauceProfile) {
          // If no profile exists, redirect to quiz first
          setCurrentView('quiz');
        } else {
          setCurrentView('profile');
        }
        break;
        
      case 'shop':
        setCurrentView('shop');
        break;
        
      case 'discover':
        if (!sauceProfile) {
          // If no profile exists, redirect to quiz first
          setCurrentView('quiz');
        } else {
          setCurrentView('discover');
        }
        break;
        
      default:
        setCurrentView(view);
    }
    
    // Scroll to top when changing views
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowPrivacy = () => {
    setCurrentView('privacy');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowTerms = () => {
    setCurrentView('terms');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowOurStory = () => {
    setCurrentView('ourstory');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cookie consent handlers
  const handleCookieAccept = (preferences: any) => {
    console.log('Cookie preferences accepted:', preferences);
    // Here you would typically:
    // 1. Initialize analytics if analytics cookies are accepted
    // 2. Set up marketing pixels if marketing cookies are accepted
    // 3. Configure preference-based features if preference cookies are accepted
  };

  const handleCookieDecline = () => {
    console.log('Cookies declined - only necessary cookies will be used');
    // Here you would ensure only necessary cookies are used
  };

  // Determine if we should show the header
  const shouldShowHeader = () => {
    const appViews = ['dashboard', 'profile', 'shop', 'discover'];
    return appViews.includes(currentView);
  };

  // Determine if we're in the DrizzID app context
  const isInApp = () => {
    const appViews = ['hero', 'quiz', 'dashboard', 'profile', 'shop', 'discover'];
    return appViews.includes(currentView);
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Coming Soon Landing Page */}
      {currentView === 'coming-soon' && (
        <ComingSoonLanding onBackToApp={() => setCurrentView('waitlist')} />
      )}

      {/* UNDRESSING SAUCE Landing Page (Main) */}
      {currentView === 'waitlist' && (
        <WaitlistPage 
          onEnterApp={handleEnterApp} 
          onShowPrivacy={handleShowPrivacy}
          onShowTerms={handleShowTerms}
          onShowOurStory={handleShowOurStory}
          onGoToShop={handleGoToShop}
        />
      )}
      
      {/* About Us Page */}
      {currentView === 'about' && (
        <AboutUs onBackToWaitlist={handleBackToWaitlist} />
      )}

      {/* Our Story Page */}
      {currentView === 'ourstory' && (
        <OurStory onBackToWaitlist={handleBackToWaitlist} />
      )}

      {/* Privacy Policy Page */}
      {currentView === 'privacy' && (
        <PrivacyPolicy onBack={handleBackToWaitlist} />
      )}

      {/* Terms & Conditions Page */}
      {currentView === 'terms' && (
        <TermsConditions onBack={handleBackToWaitlist} />
      )}
      
      {/* Header for app sections only */}
      {shouldShowHeader() && (
        <Header 
          currentView={currentView} 
          onViewChange={handleViewChange}
          hasProfile={!!sauceProfile}
        />
      )}
      
      {/* DrizzID™ App Sections */}
      {currentView === 'hero' && (
        <Hero 
          onStartQuiz={handleStartQuiz} 
          onBackToWaitlist={handleBackToWaitlist}
          hasProfile={!!sauceProfile}
          onGoToDashboard={() => handleViewChange('dashboard')}
          onGoToShop={handleGoToShop}
        />
      )}
      
      {currentView === 'quiz' && (
        <Quiz 
          onComplete={handleQuizComplete} 
          onBackToHero={() => handleViewChange('hero')}
          onBackToWaitlist={handleBackToWaitlist}
        />
      )}
      
      {currentView === 'dashboard' && sauceProfile && (
        <Dashboard 
          sauceProfile={sauceProfile}
          onRetakeQuiz={() => {
            setSauceProfile(null);
            setCurrentView('quiz');
          }}
          onGoToShop={handleGoToShop}
        />
      )}
      
      {currentView === 'profile' && sauceProfile && (
        <Profile 
          sauceProfile={sauceProfile}
          onRetakeQuiz={() => {
            setSauceProfile(null);
            setCurrentView('quiz');
          }}
        />
      )}
      
      {currentView === 'shop' && <Shop />}
      
      {currentView === 'discover' && (
        <ComingSoon
          title="Discover Coming Soon"
          subtitle="Advanced flavor discovery awaits"
          description="We're developing cutting-edge AI algorithms to help you discover new flavors based on your unique taste profile and preferences."
          onBack={() => handleViewChange('dashboard')}
          showNotifyButton={true}
          estimatedLaunch="Q3 2025"
        />
      )}

      {/* Cookie Consent Banner - Only show on main pages */}
      {!['coming-soon'].includes(currentView) && (
        <CookieConsent 
          onAccept={handleCookieAccept}
          onDecline={handleCookieDecline}
        />
      )}
    </div>
  );
}

export default App;